import { useState, useRef, useEffect, useCallback } from 'react';
import { useListenerAudioPlayMutation, useListenerAudioPlaybackMutation } from '../../hooks/api/useListenerAudio';
import { PlaybackLocation, useRateExperience, Experience, Content } from '../../hooks/api/useExperiences';
import { analytics, EventTypes } from '../../utils/analytics';
import { useEngagementEvent, useHubspotEventMutation } from '../../hooks/api/useEvent';
import useAuth from '../../hooks/utils/useAuth';

export const useAudioPlayer = (experience: Experience | Content, playbackLocation: PlaybackLocation) => {
  const { user } = useAuth();
  const audioRef = useRef<HTMLAudioElement>(null);
  const playMutation = useListenerAudioPlayMutation();
  const playBackMutation = useListenerAudioPlaybackMutation();
  const hubspotMutation = useHubspotEventMutation();
  const rateExperienceMutation = useRateExperience();
  const postEngagementEvent = useEngagementEvent();
  const [paused, setPaused] = useState<boolean>(true);
  const [percent, setPercent] = useState<number>(0);
  const [currentPlaybackId, setCurrentPlaybackId] = useState<number | undefined>();
  const [experienceRating, setExperienceRating] = useState<number>(experience.rating);
  const trackedPercents = useRef<Set<number>>(new Set());
  // Track if we've already started a new playback session (to avoid duplicate API calls)
  const hasStartedSession = useRef<boolean>(false);

  const audioStopAll = useCallback(() => {
    document.querySelectorAll('audio').forEach((el) => {
      el.pause();
      el.currentTime = 0;
    });
  }, []);

  // Reset state for a fresh playback session
  const resetPlaybackState = useCallback(() => {
    setPercent(0);
    setCurrentPlaybackId(undefined);
    trackedPercents.current.clear();
    hasStartedSession.current = false;
  }, []);

  const onPlay = useCallback(async () => {
    // If audio is already playing (not paused), don't do anything
    if (audioRef.current && !audioRef.current.paused) {
      return;
    }

    // Clear tracked percents for new playback
    trackedPercents.current.clear();

    // Only start a new playback session if we haven't already or if we're starting fresh
    if (!hasStartedSession.current) {
      hasStartedSession.current = true;
      hubspotMutation.mutate({
        event: 'play_experience',
        properties: {
          experience_id: experience.id,
          media_type: 'audio',
          playback_location: playbackLocation || 'search-results',
        },
      });
      analytics.trackEvent(EventTypes.PLAY_EXPERIENCE, experience.id, 'audio', playbackLocation);
      const playMutationResult = await playMutation.mutateAsync(
        {
          audioId: experience.id,
          listenerId: experience.listener_role_id,
        },
        {
          onSuccess: (listenerAudioPlayback) => {
            setCurrentPlaybackId(listenerAudioPlayback.id);
          },
        },
      );
      postEngagementEvent.mutate({
        engagementType: 'audio_play',
        listenerAudioPlaybackId: playMutationResult.id,
        metaData: {
          user_id: user!.id,
          experience_id: experience.id,
          playback_location: playbackLocation || 'search-results',
          duration: experience.duration,
        },
      });
    }

    if (audioRef.current) {
      // Only set currentTime if resuming from a paused position
      if (percent > 0 && percent < 100) {
        audioRef.current.currentTime = (percent / 100) * audioRef.current.duration;
      }
      audioRef.current.play();
    }
  }, [experience, playbackLocation, hubspotMutation, playMutation, postEngagementEvent, user, percent]);

  const onPause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const onEnded = useCallback(() => {
    // Reset state for next playback
    resetPlaybackState();
    setPaused(true);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  }, [resetPlaybackState]);

  const onTimeUpdate = useCallback(
    (e: React.SyntheticEvent<HTMLAudioElement>) => {
      // Don't process if we don't have a playback ID yet
      if (!currentPlaybackId) {
        return;
      }

      const currentPercent = Math.round((e.currentTarget.currentTime / experience.duration) * 100);
      const lastTimeUpdate = Math.floor(currentPercent / 10) * 10;

      // Track if we've crossed a 10% threshold
      if (!trackedPercents.current.has(lastTimeUpdate) && lastTimeUpdate <= 100) {
        trackedPercents.current.add(lastTimeUpdate);
        playBackMutation.mutate({
          audioId: currentPlaybackId,
          percentCompleted: lastTimeUpdate,
        });
      }
    },
    [currentPlaybackId, experience.duration, playBackMutation],
  );

  const handleRatingClick = useCallback(
    (newRating: number) => {
      if (experienceRating === newRating) newRating = 0;

      setExperienceRating(newRating);

      rateExperienceMutation.mutate({
        audioId: experience.id,
        listenerId: experience.listener_role_id,
        rating: newRating,
      });
    },
    [experienceRating, experience.id, experience.listener_role_id, rateExperienceMutation],
  );

  // Set up event listeners for the audio element
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = (event: Event) => {
      const element = event.target as HTMLAudioElement;
      if (element.duration > 0) {
        setPercent(parseFloat(((element.currentTime / element.duration) * 100).toPrecision(3)));
      }
    };

    const handleEnded = () => {
      resetPlaybackState();
      setPaused(true);
    };

    const handlePause = () => {
      setPaused(true);
    };

    const handlePlay = () => {
      setPaused(false);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('play', handlePlay);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('play', handlePlay);
    };
  }, [resetPlaybackState]);

  return {
    audioRef,
    paused,
    percent,
    experienceRating,
    audioStopAll,
    onPlay,
    onPause,
    onEnded,
    onTimeUpdate,
    handleRatingClick,
  };
};
